/**
 * Название позиции счёта — `01-BUSINESS-RULES.md` §2.1.
 *
 * Формат:
 *   Изготовление ортодонтического протеза ({конструкция}{ + доп. элементы}, пац. {ФИО}, врач {ФИО})
 *
 * Пациент и врач — по выключателям. Пациент по умолчанию есть, врач — нет:
 * так выставлялись все счета до сих пор. Некоторые клиники сверяют счёт по
 * врачам, и им нужна приписка «врач …»; другим не нужно ни одной фамилии.
 * Выключатели стоят у юрлица в карточке клиники и в окне нового счёта.
 *
 * Доп. элементы ОБЯЗАТЕЛЬНО перечисляются в названии через « + ». Это не
 * оформительская мелочь: без них в счёте появляется необъяснимая сумма —
 * клиника видит «Ретенционная каппа — 5 400 ₽» при прайсовой цене 2 000 ₽
 * и не понимает, откуда разница. Это было найдено как реальный баг.
 */
/**
 * Приписка о челюсти в названии работы.
 *
 * Сокращения — те же, что лаборатория пишет от руки в таблицах учёта.
 * Разворачивать их в «на верхнюю челюсть» нельзя: название позиции счёта
 * и так длинное, а в наряде оно живёт в узкой колонке.
 */
const JAW_SUFFIX = {
    'Верхняя челюсть': 'на верх.чел.',
    'Нижняя челюсть': 'на нижн.чел.',
    /*
     * Обе челюсти — ДВА аппарата, и в названии работы это сказано словами:
     * «Ретенционная каппа на верх.чел. и нижн.чел.». Врач и бухгалтерия
     * читают позицию счёта, а не количество рядом с ней, и «×2» без
     * пояснения выглядит как две одинаковые каппы на одну челюсть.
     */
    'Обе челюсти': 'на верх.чел. и нижн.чел.',
};
/**
 * Дописывает челюсть к названию ОСНОВНОЙ конструкции.
 *
 * Именно к ней, а не в конец всего перечня: челюсть — свойство изготавливаемого
 * аппарата, а не заказа. «СПА базис + окклюзионные накладки на верх.чел. +
 * Печать 3D модели»: верхняя челюсть у базиса, печать модели тут ни при чём.
 * В конце строки приписка читалась бы как относящаяся к последнему доп.
 * элементу — а их в заказе бывает несколько.
 *
 * Только если есть к ЧЕМУ дописывать: у заказа без выбранной конструкции
 * приписка осталась бы висеть сама по себе — «(на верх.чел., пац. Иванова)»
 * читается как потерянное название изделия.
 */
function withJaw(text, jaw) {
    if (!text || !jaw)
        return text;
    return `${text} ${JAW_SUFFIX[jaw]}`;
}
/**
 * Приписка о челюсти отдельной строкой.
 *
 * Нужна там, где название работы собирается ПО ЧАСТЯМ, а не одной строкой:
 * в журнале заказов конструкция, количество и доп. элементы выводятся
 * разными кусками размера и цвета. Сокращения берутся отсюда же, что и
 * в документах, — иначе в таблице и в счёте они разъехались бы.
 */
export function jawText(jaw) {
    return jaw ? JAW_SUFFIX[jaw] : '';
}
/**
 * Название позиции счёта для заказа.
 *
 * Количество доп. элемента указывается как «×N» только при N > 1 — «×1»
 * в документе выглядит как ошибка набора.
 */
export function composeItemName(order, catalog, options = {}) {
    /*
     * Пустой идентификатор и НЕНАЙДЕННЫЙ — разные случаи, и путать их нельзя.
     *
     * Пусто — конструкцию ещё не выбрали, писать нечего. Ненайденный —
     * позицию удалили из справочника уже после заказа: в счёте должно
     * остаться хотя бы слово «конструкция», иначе строка потеряет предмет.
     */
    const found = order.constructionId
        ? (catalog.get(order.constructionId)?.name ?? 'конструкция')
        : '';
    /*
     * Количество приписывается «×N» ровно так же, как у доп. элементов, и
     * ровно по той же причине (§2.1): без него в счёте появляется сумма,
     * вдвое больше прайсовой, и клиника не понимает, откуда разница.
     * «×1» не пишется — в документе это выглядит опечаткой.
     */
    const qty = order.constructionQty ?? 1;
    /*
     * Когда количество вынесено в колонку «Кол-во», в названии его быть не
     * должно: одно и то же число, написанное дважды, читается как две разные
     * величины — «×2 штуки по 2 штуки».
     */
    const named = withJaw(found, order.jaw);
    const base = qty > 1 && !options.qtyInColumn ? `${named} ×${qty}` : named;
    /*
     * Конструкция может быть НЕ ВЫБРАНА: заказ заводят по звонку, когда изделие
     * ещё не названо. Тогда в скобках остаются только доп. элементы и пациент,
     * а если и их нет — скобок не появляется вовсе. Пустые «()» в счёте
     * выглядели бы как потерянные данные.
     */
    const parts = [base, extrasList(order.extras, catalog)].filter(Boolean).join(' + ');
    /*
     * Фамилии — после работы, через запятую: сначала пациент, потом врач.
     * Выключенная или пустая фамилия не оставляет после себя висящей запятой.
     */
    const patient = options.patient !== false && order.patient.trim() ? `пац. ${order.patient.trim()}` : '';
    const doctor = options.doctor && order.doctor?.trim() ? `врач ${order.doctor.trim()}` : '';
    const inside = [parts, patient, doctor].filter(Boolean).join(', ');
    return inside
        ? `Изготовление ортодонтического протеза (${inside})`
        : 'Изготовление ортодонтического протеза';
}
/** Перечень доп. элементов через « + », с количеством при N > 1. */
function extrasList(extras, catalog) {
    return (extras ?? [])
        .map((extra) => {
        const found = catalog.get(extra.id);
        if (!found)
            return null;
        return extra.qty > 1 ? `${found.name} ×${extra.qty}` : found.name;
    })
        .filter((s) => s !== null)
        .join(' + ');
}
/**
 * Что изготавливается: конструкция и доп. элементы через « + ».
 *
 * Без обёртки «Изготовление ортодонтического протеза (…)» — это для писем
 * врачу, где фраза строится своя: «Заказ на пациента Иванова (Небный бюгель +
 * Печать 3D модели) принят в работу».
 *
 * Раньше в письмо шло ОДНО название базовой конструкции. Доп. элементы —
 * печать модели, крючки, искусственные зубы — врач в письме не видел, хотя
 * заказывал их сам и по ним же сверяет полученное.
 */
export function constructionText(order, catalog) {
    const base = order.constructionId ? (catalog.get(order.constructionId)?.name ?? 'конструкция') : '';
    const qty = order.constructionQty ?? 1;
    const named = withJaw(base, order.jaw);
    const withQty = qty > 1 ? `${named} ×${qty}` : named;
    return [withQty, extrasList(order.extras, catalog)].filter(Boolean).join(' + ');
}
/** Индекс справочника для `composeItemName`. */
export function nameIndex(constructions) {
    return new Map(constructions.map((c) => [c.id, c]));
}
// ---------------------------------------------------------------------------
// Сборка позиций счёта из заказов
// ---------------------------------------------------------------------------
/**
 * Название сводной строки, когда все работы объединяются в одну.
 *
 * Множественное число и обобщённая формулировка — за строкой стоит месяц
 * работы, а не одно изделие.
 */
export const MERGED_ITEM_NAME = 'Изготовление ортодонтических протезов (аппаратов)';
/**
 * Позиции счёта по выбранным заказам.
 *
 * Два режима, и выбор между ними — свойство ЮРЛИЦА, а не решение в момент
 * выставления:
 *
 *   - обычный: одна позиция на заказ, с пациентом и составом работы. Клиника
 *     сверяет счёт по фамилиям;
 *   - сводный (`merge`): одна строка на весь счёт с общей суммой. Так платят
 *     клиники, рассчитывающиеся за месяц целиком: перечислять там три десятка
 *     заказов пофамильно незачем, а в наряд они всё равно не помещаются.
 *
 * Позиции в любом случае остаются независимым снимком (§2.1) — после создания
 * счёта их можно править, добавлять и удалять.
 */
export function buildInvoiceItems(orders, catalog, options = {}) {
    if (options.merge) {
        // Пустой список не даёт сводной строки: счёт без позиций не создать,
        // а строка с нулём выглядит как ошибка.
        if (orders.length === 0)
            return [];
        return [
            {
                id: 'merged',
                name: MERGED_ITEM_NAME,
                qty: 1,
                unit: '',
                priceKop: orders.reduce((sum, order) => sum + order.costKop, 0),
            },
        ];
    }
    return orders.map((order) => {
        const qty = order.constructionQty ?? 1;
        const extras = order.extras ?? [];
        /*
         * Количество попадает в колонку «Кол-во» только тогда, когда оно там
         * ЧЕСТНО умножает цену: заказ на несколько одинаковых аппаратов и ничего
         * сверх них. Тогда счёт читается как положено — «2 × 1000 = 2000».
         *
         * Если в заказе есть доп. элементы, цена позиции складывается из разных
         * вещей, и её деление на количество даёт число, которое не является ценой
         * ничего: «Печать ×2 + винт» за 2500 превратились бы в «2 по 1250».
         * Тогда количество остаётся в названии, а в колонке стоит единица и
         * полная сумма — так и было до появления количества.
         *
         * Делимость проверяется отдельно: при неровном делении копейки ушли бы
         * в округление, и «кол-во × цена» перестало бы сходиться с суммой —
         * а расхождение в счёте хуже любой неясности.
         */
        const inColumn = qty > 1 && extras.length === 0 && order.costKop % qty === 0;
        return {
            id: order.id,
            name: composeItemName(order, catalog, {
                qtyInColumn: inColumn,
                patient: options.patient,
                doctor: options.doctor,
            }),
            qty: inColumn ? qty : 1,
            unit: '',
            priceKop: inColumn ? order.costKop / qty : order.costKop,
        };
    });
}
// ---------------------------------------------------------------------------
// Переключение фамилий в УЖЕ ВЫСТАВЛЕННОМ счёте
// ---------------------------------------------------------------------------
/**
 * Как строка счёта выглядит при каждом сочетании выключателей.
 *
 * Количество в колонке считается ровно так же, как в `buildInvoiceItems`:
 * от фамилий оно не зависит, а расходиться эти два места не должны.
 */
function nameVariants(order, catalog) {
    const qty = order.constructionQty ?? 1;
    const extras = order.extras ?? [];
    const qtyInColumn = qty > 1 && extras.length === 0 && order.costKop % qty === 0;
    const combos = [
        { patient: true, doctor: true },
        { patient: true, doctor: false },
        { patient: false, doctor: true },
        { patient: false, doctor: false },
    ];
    return combos.map((people) => ({
        people,
        name: composeItemName(order, catalog, { qtyInColumn, ...people }),
    }));
}
/**
 * Переписывает названия строк под новые выключатели фамилий.
 *
 * Счёт НЕ пересобирается заново, как при выставлении: в нём уже могли
 * появиться доставка, скидка и правки формулировок (§2.1), и сборка с нуля
 * стёрла бы их. Поэтому каждая строка сверяется с заказами: если её название
 * в точности совпадает с тем, что даёт какой-нибудь заказ счёта при
 * КАКОМ-ЛИБО сочетании фамилий, — это машинная строка, и её можно переписать.
 * Всё остальное — рукописное, и его не трогают.
 *
 * Заказ засчитывается один раз: два одинаковых заказа дают две одинаковые
 * строки, и вторая должна найти себе вторую, а не ту же самую.
 */
export function retitleInvoiceItems(items, orders, catalog, people) {
    const variants = orders.map((order) => nameVariants(order, catalog));
    const used = new Set();
    return items.map((item) => {
        const index = variants.findIndex((list, i) => !used.has(i) && list.some((v) => v.name === item.name));
        const list = variants[index];
        if (index < 0 || !list)
            return item;
        used.add(index);
        const wanted = list.find((v) => v.people.patient === (people.patient !== false) && v.people.doctor === (people.doctor === true));
        return wanted ? { ...item, name: wanted.name } : item;
    });
}
/**
 * С какими фамилиями счёт выставлен на самом деле.
 *
 * Выключатели в карточке клиники с тех пор могли поменять, а счёт остался
 * прежним — показывать по ним значит соврать: человек увидит «врач включён»
 * там, где врача в строках нет. Поэтому положение считывается с самих строк.
 *
 * Голосуют все опознанные строки, а не первая: пустая фамилия пациента ничего
 * не добавляет к названию, и такая строка подошла бы под любое сочетание.
 * Если не опознана ни одна — берётся значение юрлица.
 */
export function detectInvoicePeople(items, orders, catalog, fallback) {
    let patient = 0;
    let doctor = 0;
    let seen = 0;
    const variants = orders.map((order) => nameVariants(order, catalog));
    const used = new Set();
    for (const item of items) {
        const index = variants.findIndex((list, i) => !used.has(i) && list.some((v) => v.name === item.name));
        const list = variants[index];
        if (index < 0 || !list)
            continue;
        used.add(index);
        seen += 1;
        /*
         * У строки может подойти несколько сочетаний сразу — когда фамилии
         * пустые. Такая строка не голосует ни за что: засчитывается только
         * ОДНОЗНАЧНОЕ совпадение, где фамилия действительно видна.
         */
        const matched = list.filter((v) => v.name === item.name);
        if (matched.every((v) => v.people.patient))
            patient += 1;
        else if (matched.every((v) => !v.people.patient))
            patient -= 1;
        if (matched.every((v) => v.people.doctor))
            doctor += 1;
        else if (matched.every((v) => !v.people.doctor))
            doctor -= 1;
    }
    if (seen === 0)
        return fallback;
    return {
        patient: patient === 0 ? fallback.patient : patient > 0,
        doctor: doctor === 0 ? fallback.doctor : doctor > 0,
    };
}
//# sourceMappingURL=item-name.js.map